// presentation-2 validation (presentation-2 contract sections 3, 6.2, 7 and 9): scene and targets
// structure, plus reference checks and direct-play coverage against recorded fixtures. The client
// and the Node tools run the same checks.

import { Checker, ID, type Issue } from './validate.ts';
import { parseSelector, selectCounters, selectZones, type Selector } from './selectors.ts';
import { BRICKS, MATERIALS, SCENE_LIMITS, type BrickSchema, type PropType } from '../bricks/schema.ts';
import type { BrickInstance, Scene, SceneFile, TargetsFile, Views } from './types.ts';
import { anchorKey, coverage, resolveTargets } from '../play/targets.ts';
import { buildTable } from '../model/table.ts';
import { buildChoices } from '../model/choices.ts';
import type { Description, Observation } from '../protocol/ui.ts';
import { validateGuide } from '../catalog/guide.ts';

const TRACK = /^(auto|\d{1,4}px|\d{1,2}(\.\d{1,2})?fr|minmax\(\d{1,4}px, ?\d{1,2}(\.\d{1,2})?fr\))$/;
const AREA_NAME = /^[a-z][a-z0-9-]{0,31}$/;
const SEAT_REF = /^(\{self\}|\{seat\}|[A-Za-z0-9][A-Za-z0-9:_.\-]{0,127})$/;

export function validateScene(value: unknown): Issue[] {
  const c = new Checker('scene.json');
  if (!c.object(value, '$', ['scene'], ['narrow', 'solo', 'guide'])) return c.issues;
  const file = value as unknown as SceneFile;
  scene(c, file.scene, '$.scene');
  if (file.narrow !== undefined) scene(c, file.narrow, '$.narrow');
  if (file.solo !== undefined) scene(c, file.solo, '$.solo');
  if (file.guide !== undefined) c.issues.push(...validateGuide(file.guide));
  return c.issues;
}

function scene(c: Checker, s: Scene, path: string): void {
  if (!c.object(s, path, ['areas', 'columns', 'rows', 'place', 'rest'], ['minWidth', 'views', 'prompt', 'promptTone', 'toast', 'flow', 'shell', 'backgrounds', 'omitBoards', 'touch'])) return;
  if (s.shell !== undefined) c.oneOf(s.shell, `${path}.shell`, ['compact-table']);
  if (s.flow !== undefined) c.oneOf(s.flow, `${path}.flow`, ['page']);
  if (s.touch !== undefined && typeof s.touch !== 'boolean') c.error(`${path}.touch`, 'Expected true or false.');
  if (s.omitBoards !== undefined && c.array(s.omitBoards, `${path}.omitBoards`, 1, 8)) s.omitBoards.forEach((id, i) => c.string(id, `${path}.omitBoards[${i}]`, 128, ID));
  if (s.minWidth !== undefined) c.number(s.minWidth, `${path}.minWidth`, 320, 4000, true);
  const names = new Set<string>();
  let width = -1;
  if (c.array(s.areas, `${path}.areas`, 1, SCENE_LIMITS.rows)) s.areas.forEach((row, i) => {
    if (!c.string(row, `${path}.areas[${i}]`, 256)) return;
    const cells = row.trim().split(/\s+/);
    if (cells.length > SCENE_LIMITS.columns) c.error(`${path}.areas[${i}]`, `At most ${SCENE_LIMITS.columns} columns.`);
    if (width >= 0 && cells.length !== width) c.error(`${path}.areas[${i}]`, 'Every row needs the same number of cells.');
    width = cells.length;
    for (const cell of cells) { if (cell !== '.' && !AREA_NAME.test(cell)) c.error(`${path}.areas[${i}]`, `"${cell}" is not an area name.`); if (cell !== '.') names.add(cell); }
  });
  for (const name of names) if (!rectangular(s.areas, name)) c.error(`${path}.areas`, `Area "${name}" is not a rectangle.`);
  if (c.array(s.columns, `${path}.columns`, 1, SCENE_LIMITS.columns)) {
    s.columns.forEach((t, i) => { if (typeof t !== 'string' || !TRACK.test(t)) c.error(`${path}.columns[${i}]`, 'Use auto, Npx, Nfr or minmax(Npx, Nfr).'); });
    if (width > 0 && s.columns.length !== width) c.error(`${path}.columns`, 'One track per area column.');
  }
  if (c.array(s.rows, `${path}.rows`, 1, SCENE_LIMITS.rows)) {
    s.rows.forEach((t, i) => { if (typeof t !== 'string' || !TRACK.test(t)) c.error(`${path}.rows[${i}]`, 'Use auto, Npx, Nfr or minmax(Npx, Nfr).'); });
    if (Array.isArray(s.areas) && s.rows.length !== s.areas.length) c.error(`${path}.rows`, 'One track per area row.');
  }
  if (typeof s.rest !== 'string' || !names.has(s.rest)) c.error(`${path}.rest`, 'Name an area that receives unplaced zones.');
  if (s.shell === 'compact-table') for (const required of ['players', 'turn', 'board', 'hand', 'side'])
    if (!names.has(required)) c.error(`${path}.areas`, `Compact table needs the ${required} area.`);
  if (s.backgrounds !== undefined && c.object(s.backgrounds, `${path}.backgrounds`, [], [...names]))
    for (const [area, source] of Object.entries(s.backgrounds))
      c.string(source, `${path}.backgrounds.${area}`, 134, /^asset:[A-Za-z0-9][A-Za-z0-9:_./-]{0,127}$/);
  const count = { instances: 0 };
  if (c.object(s.place, `${path}.place`, [], [...names])) {
    for (const [area, list] of Object.entries(s.place)) {
      if (c.array(list, `${path}.place.${area}`, 0, SCENE_LIMITS.instances)) list.forEach((b, i) => instance(c, b, `${path}.place.${area}[${i}]`, 1, false, count));
    }
  }
  if (s.prompt !== undefined) c.oneOf(s.prompt, `${path}.prompt`, ['header', 'floating']);
  if (s.promptTone !== undefined) c.oneOf(s.promptTone, `${path}.promptTone`, ['light', 'dark']);
  if (s.toast !== undefined) c.oneOf(s.toast, `${path}.toast`, ['top', 'corner']);
  if (s.views !== undefined) views(c, s.views, `${path}.views`, names, count);
  if (count.instances > SCENE_LIMITS.instances) c.error(`${path}.place`, `At most ${SCENE_LIMITS.instances} brick instances.`);
  if (promptPlace(s) === 'header' && !Object.values(s.place ?? {}).flat().some(b => b?.brick === 'hud'))
    c.warn(`${path}.prompt`, 'A header prompt needs a hud brick; without one the prompt floats.');
}

/** Where a scene puts the turn prompt: `header` by default when it has views, else `floating`. */
export function promptPlace(s: Pick<Scene, 'prompt' | 'views'>): 'header' | 'floating' {
  return s.prompt ?? (s.views ? 'header' : 'floating');
}

const TAB_KEY = /^[1-9a-z]$/;

function views(c: Checker, v: Views, path: string, names: Set<string>, count: { instances: number }): void {
  if (!c.object(v, path, ['area', 'tabs'], ['autoFocus', 'material', 'tint', 'badges'])) return;
  if (v.material !== undefined) c.oneOf(v.material, `${path}.material`, MATERIALS);
  if (v.tint !== undefined) c.string(v.tint, `${path}.tint`, 7, /^#[0-9a-fA-F]{6}$/);
  if (typeof v.area !== 'string' || !names.has(v.area)) c.error(`${path}.area`, 'Name an area of this scene.');
  if (v.autoFocus !== undefined) c.oneOf(v.autoFocus, `${path}.autoFocus`, ['single-view-targets', 'none']);
  if (v.badges !== undefined) c.oneOf(v.badges, `${path}.badges`, ['choices']);
  if (!c.array(v.tabs, `${path}.tabs`, 1, SCENE_LIMITS.tabs)) return;
  const ids = new Set<string>(), keys = new Set<string>();
  v.tabs.forEach((t, i) => {
    const p = `${path}.tabs[${i}]`;
    if (!c.object(t, p, ['id', 'label', 'bricks'], ['key', 'badge', 'icon', 'description', 'perSeat', 'groupLabel', 'showWhen', 'background', 'passive'])) return;
    if (t.background !== undefined) c.string(t.background, `${p}.background`, 134, /^asset:[A-Za-z0-9][A-Za-z0-9:_./-]{0,127}$/);
    if (t.showWhen !== undefined) c.oneOf(t.showWhen, `${p}.showWhen`, ['targets', 'zones']);
    if (t.passive !== undefined && typeof t.passive !== 'boolean') c.error(`${p}.passive`, 'Expected true or false.');
    if (c.string(t.id, `${p}.id`, 32, /^[a-z][a-z0-9-]{0,31}$/) && ids.has(t.id)) c.error(`${p}.id`, 'Duplicate tab ID.');
    ids.add(t.id);
    c.string(t.label, `${p}.label`, 40);
    if (t.perSeat !== undefined) c.oneOf(t.perSeat, `${p}.perSeat`, ['opponents']);
    if (t.groupLabel !== undefined) {
      c.string(t.groupLabel, `${p}.groupLabel`, 40);
      if (t.perSeat !== 'opponents') c.error(`${p}.groupLabel`, 'Grouping requires perSeat: opponents.');
      if (typeof t.groupLabel === 'string' && (!t.groupLabel.trim() || t.groupLabel.includes('{seat}'))) c.error(`${p}.groupLabel`, 'Use nonblank plain text without {seat}.');
    }
    if (t.perSeat === undefined && typeof t.label === 'string' && t.label.includes('{seat}')) c.error(`${p}.label`, '"{seat}" is only valid in a perSeat tab.');
    if (t.badge !== undefined) c.string(t.badge, `${p}.badge`, 16);
    if (t.icon !== undefined) c.string(t.icon, `${p}.icon`, 64, /^[A-Za-z][A-Za-z0-9_-]{0,63}$/);
    if (t.description !== undefined) c.string(t.description, `${p}.description`, 160);
    if (t.key !== undefined) {
      if (typeof t.key !== 'string' || !TAB_KEY.test(t.key) || t.key === 'm') c.error(`${p}.key`, 'Use one character 1-9 or a-z other than m (the moves drawer).');
      else if (keys.has(t.key)) c.error(`${p}.key`, 'Duplicate tab key.');
      else keys.add(t.key);
    }
    if (c.array(t.bricks, `${p}.bricks`, 1, SCENE_LIMITS.instances)) t.bricks.forEach((b, j) => {
      instance(c, b, `${p}.bricks[${j}]`, 1, t.perSeat !== undefined, count);
    });
  });
}

function rectangular(areas: string[], name: string): boolean {
  if (!Array.isArray(areas)) return true;
  const cells: [number, number][] = [];
  areas.forEach((row, r) => typeof row === 'string' && row.trim().split(/\s+/).forEach((cell, col) => { if (cell === name) cells.push([r, col]); }));
  const rows = cells.map(x => x[0]), cols = cells.map(x => x[1]);
  const h = Math.max(...rows) - Math.min(...rows) + 1, w = Math.max(...cols) - Math.min(...cols) + 1;
  return h * w === cells.length;
}

function instance(c: Checker, b: BrickInstance, path: string, depth: number, inRepeat: boolean, count: { instances: number }): void {
  count.instances++;
  if (depth > SCENE_LIMITS.depth) { c.error(path, `Bricks may nest at most ${SCENE_LIMITS.depth} levels.`); return; }
  if (!b || typeof b !== 'object') { c.error(path, 'Expected a brick instance.'); return; }
  if (b.brick === 'repeat') {
    if (!c.object(b, path, ['brick', 'over', 'child'], ['order', 'select'])) return;
    if (b.select !== undefined) checkSelect(c, BRICKS['resource-tray']!, b, path, false);
    c.oneOf(b.over, `${path}.over`, ['seats', 'opponents']);
    if (b.order !== undefined) c.oneOf(b.order, `${path}.order`, ['self-first', 'seat-order']);
    if (inRepeat) c.error(path, 'A repeat cannot contain another repeat.');
    instance(c, b.child!, `${path}.child`, depth + 1, true, count);
    return;
  }
  const schema = BRICKS[b.brick];
  if (!schema) { c.error(`${path}.brick`, `Unknown brick "${String(b.brick)}".`); return; }
  if (!c.object(b, path, ['brick'], ['select', 'seat', 'props'])) return;
  checkSelect(c, schema, b, path, inRepeat);
  if (b.seat !== undefined) {
    if (!schema.seat) c.error(`${path}.seat`, `${b.brick} takes no seat.`);
    else if (typeof b.seat !== 'string' || !SEAT_REF.test(b.seat)) c.error(`${path}.seat`, 'Use {self}, {seat} or a seat ID.');
    else if (b.seat === '{seat}' && !inRepeat) c.error(`${path}.seat`, '"{seat}" is only valid inside a repeat.');
  } else if (schema.seat === 'required' && !inRepeat) c.error(`${path}.seat`, `${b.brick} needs a seat.`);
  if (b.props !== undefined && c.object(b.props, `${path}.props`, [], Object.keys(schema.props))) {
    for (const [key, value] of Object.entries(b.props)) {
      const type = schema.props[key];
      if (!type) continue;
      if (type.type === 'bricks') {
        if (c.array(value, `${path}.props.${key}`, 0, 16)) (value as BrickInstance[]).forEach((child, i) => instance(c, child, `${path}.props.${key}[${i}]`, depth + 1, inRepeat || !!b.seat, count));
      } else prop(c, type, value, `${path}.props.${key}`);
    }
  }
  if (b.brick === 'action-tiles' && Array.isArray(b.props?.tiles)) (b.props.tiles as unknown[]).forEach((t, i) => {
    const parts = typeof t === 'string' ? t.split('|') : [];
    if (parts.length < 2 || parts.length > 3 || !/^[a-z][a-z0-9-]{0,31}$/.test(parts[0]!) || !parts[1] || parts[1].length > 32 || (parts[2]?.length ?? 0) > 96)
      c.error(`${path}.props.tiles[${i}]`, 'Use "id|Title" or "id|Title|Description" (id like village, title up to 32 characters, description up to 96).');
  });
  if (b.brick === 'opponent-strip') {
    for (const [i, entry] of ((b.props?.counterHelp as string[] | undefined) ?? []).entries()) {
      const divider = entry.indexOf('|');
      if (divider < 1 || divider > 64 || divider === entry.length - 1) c.error(`${path}.props.counterHelp[${i}]`, 'Use counterKey|game-rule explanation.');
    }
    for (const [i, entry] of ((b.props?.portraits as string[] | undefined) ?? []).entries()) {
      if (!/^[A-Za-z0-9:_-]+\|asset:[A-Za-z0-9][A-Za-z0-9:_./-]{0,127}$/.test(entry)) c.error(`${path}.props.portraits[${i}]`, 'Use seatId|asset:ID.');
    }
  }
  if (b.brick === 'rules-guide') rulesGuide(c, b, path);
  if (b.brick === 'partition-board') {
    if (b.props?.bodySource === 'catalog-text' && (b.props.mode !== 'readOnly' || b.props.bodyField !== undefined || b.props.bodyFields !== undefined)) c.error(`${path}.props.bodySource`, 'Catalog text requires readOnly mode and no field binding.');
    if (b.props?.bodySource === 'fields') {
      const fields = b.props.bodyFields;
      if (b.props.mode !== 'readOnly' || b.props.bodyField !== undefined || !Array.isArray(fields) || fields.length < 1 || fields.length > 8 || new Set(fields).size !== fields.length || fields.some(f => typeof f !== 'string' || !ID.test(f))) c.error(`${path}.props.bodyFields`, 'Ordered fields require readOnly mode, no bodyField and one to eight distinct exact field IDs.');
    } else if (b.props?.bodyFields !== undefined) c.error(`${path}.props.bodyFields`, 'Use bodySource fields for an ordered field binding.');
    if (typeof b.props?.scopeFrom === 'string') {
      const scope = parseSelector(b.props.scopeFrom);
      if (typeof scope === 'string' || scope.kind !== 'counter') c.error(`${path}.props.scopeFrom`, 'Use one public counter selector.');
    }
  }
}

function checkSelect(c: Checker, schema: BrickSchema, b: BrickInstance, path: string, inRepeat: boolean): void {
  if (b.select === undefined) {
    if (schema.selectRequired) c.error(`${path}.select`, `${b.brick} needs a select.`);
    return;
  }
  if (schema.select === 'none') { c.error(`${path}.select`, `${b.brick} takes no select.`); return; }
  if (!c.string(b.select, `${path}.select`, 160)) return;
  const s = parseSelector(b.select);
  if (typeof s === 'string') { c.error(`${path}.select`, s); return; }
  if (b.select.includes('{seat}') && !inRepeat) c.error(`${path}.select`, '"{seat}" is only valid inside a repeat or a seat brick.');
  if (!selectFits(schema.select, s)) c.error(`${path}.select`, `${b.brick} needs a ${schema.select} selector.`);
}

function selectFits(kind: BrickSchema['select'], s: Selector): boolean {
  const zone = s.kind === 'zone';
  const zones = ['zone', 'zonePrefix', 'zonesOwner', 'zonesKind', 'rest'].includes(s.kind);
  const counters = ['countersOwner', 'countersKey', 'countersId', 'counter'].includes(s.kind);
  switch (kind) {
    case 'zone': return zone;
    case 'zones': return zones;
    case 'board': return s.kind === 'board';
    case 'counters': return counters;
    case 'pile': return zone || s.kind === 'counter';
    case 'track': return zone || counters;
    case 'none': return false;
  }
}

function prop(c: Checker, type: PropType, value: unknown, path: string): void {
  switch (type.type) {
    case 'enum': c.oneOf(value, path, type.values); break;
    case 'int': c.number(value, path, type.min, type.max, true); break;
    case 'number': c.number(value, path, type.min, type.max); break;
    case 'bool': if (typeof value !== 'boolean') c.error(path, 'Expected true or false.'); break;
    case 'string': c.string(value, path, type.max); break;
    case 'strings':
      if (c.array(value, path, type.min ?? 1, type.max)) (value as unknown[]).forEach((v, i) => { if (type.values) c.oneOf(v, `${path}[${i}]`, type.values); else c.string(v, `${path}[${i}]`, type.itemMax??128); });
      break;
    case 'selector':
      if (c.string(value, path, 160)) { const s = parseSelector(value as string); if (typeof s === 'string') c.error(path, s); }
      break;
    case 'bricks': break;
  }
}

export function validateTargets(value: unknown): Issue[] {
  const c = new Checker('targets.json');
  if (!c.object(value, '$', ['targets'])) return c.issues;
  const t = value as unknown as TargetsFile;
  if (!c.array(t.targets, '$.targets', 0, 256)) return c.issues;
  t.targets.forEach((r, i) => {
    const p = `$.targets[${i}]`;
    if (!c.object(r, p, ['match', 'anchor'], ['button', 'bar', 'attention', 'priority', 'also'])) return;
    if (c.object(r.match, `${p}.match`, [], ['kind', 'data', 'group'])) {
      if (r.match.kind !== undefined) c.string(r.match.kind, `${p}.match.kind`, 128, ID);
      if (r.match.group !== undefined) c.string(r.match.group, `${p}.match.group`, 64);
      if (r.match.data !== undefined && c.object(r.match.data, `${p}.match.data`, [], Object.keys(r.match.data as object)))
        for (const [k, v] of Object.entries(r.match.data)) if (!['string', 'number', 'boolean'].includes(typeof v)) c.error(`${p}.match.data.${k}`, 'Expected a string, number or boolean.');
      if (Object.keys(r.match).length === 0) c.warn(`${p}.match`, 'An empty match routes every remaining choice to one anchor.');
    }
    if (c.string(r.anchor, `${p}.anchor`, 160) && !/^(pile|mat|counter|slot|zone|pool|tile):.+/.test(r.anchor)) c.error(`${p}.anchor`, 'Use <type>:<selector> with type pile, mat, counter, slot, zone, pool or tile.');
    if (r.button !== undefined) c.string(r.button, `${p}.button`, 32);
    if (r.bar !== undefined && typeof r.bar !== 'boolean') c.error(`${p}.bar`, 'Expected true or false.');
    if (r.attention !== undefined && typeof r.attention !== 'boolean') c.error(`${p}.attention`, 'Expected true or false.');
    if (r.attention && !r.bar) c.error(`${p}.attention`, 'Attention requires a turn-bar action.');
    if (r.priority !== undefined && typeof r.priority !== 'boolean') c.error(`${p}.priority`, 'Expected true or false.');
    if (r.also !== undefined && typeof r.also !== 'boolean') c.error(`${p}.also`, 'Expected true or false.');
  });
  return c.issues;
}

// ---- Reference checks and coverage ------------------------------------------------------

export interface CoverageReport { fixture: string; choices: number; resolved: number; coverage: number; unresolved: string[] }

/** Selects every scene brick against each fixture and computes direct-play coverage per fixture. */
export function validateSceneReferences(
  pkg: { scene: SceneFile; targets: TargetsFile },
  description: Description,
  fixtures: { name: string; observation: Observation; seat: string | null }[],
): { issues: Issue[]; coverage: CoverageReport[] } {
  const c = new Checker('references');
  const reports: CoverageReport[] = [];
  const matched = new Map<string, number>();
  const anchorsSeen = new Set<string>();
  // Tile anchors must name a tile an action-tiles brick declares.
  const tiles = new Set<string>();
  for (const s of [pkg.scene.scene, ...(pkg.scene.narrow ? [pkg.scene.narrow] : []), ...(pkg.scene.solo ? [pkg.scene.solo] : [])]) walkScene(s, [], null, b => { if (b.brick === 'action-tiles') for (const t of (b.props?.tiles as string[] | undefined) ?? []) tiles.add(t.split('|')[0]!); });
  for (const r of pkg.targets.targets) if (r.anchor.startsWith('tile:') && !tiles.has(r.anchor.slice(5))) c.error(`targets ${r.anchor}`, 'No action-tiles brick declares this tile.');
  for (const { name, observation, seat: viewer } of fixtures) {
    const self = viewer;
    const table = buildTable(observation, description, self);
    for (const s of [pkg.scene.scene, ...(pkg.scene.narrow ? [pkg.scene.narrow] : []), ...(pkg.scene.solo ? [pkg.scene.solo] : [])]) walkScene(s, table.seats.map(x => x.id), self, (b, seat, where) => {
      if (!b.select) return;
      const sel = parseSelector(b.select);
      if (typeof sel === 'string' || sel.kind === 'rest' || sel.kind === 'events') return;
      const hits = selectZones(sel, table.zones, seat, self, new Set()).length + selectCounters(sel, table.counters, seat, self).length;
      matched.set(where, (matched.get(where) ?? 0) + hits);
      if (BRICKS[b.brick]?.select === 'board' && sel.kind === 'board' && !description.boards.some(x => x.id === sel.id)) c.error(where, 'No Describe board has this ID.');
    });
    const choices = buildChoices(observation, [], new Set(table.objects.keys()));
    const map = resolveTargets(choices, table, pkg.targets.targets);
    for (const key of map.anchors.keys()) anchorsSeen.add(key.slice(0, key.indexOf(':')));
    for (const r of pkg.targets.targets) {
      const key = anchorKey(r.anchor, table);
      if (typeof key !== 'string' && !(r.anchor.includes('{self}') && !self)) c.warn(`targets ${r.anchor}`, `${name}: ${key.error}`);
    }
    reports.push({ fixture: name, choices: choices.length, resolved: choices.length - map.unresolved.length, coverage: coverage(map, choices.length), unresolved: map.unresolved.map(u => u.label) });
  }
  for (const [where, hits] of matched) if (hits === 0 && !where.includes('{self}')) c.warn(where, 'Matches nothing in the recorded observations.');
  for (const r of reports) if (r.unresolved.length) c.warn(`coverage ${r.fixture}`, `No table target for: ${r.unresolved.join('; ')}`);
  return { issues: c.issues, coverage: reports };
}

function walkScene(s: Scene, seats: string[], self: string | null, visit: (b: BrickInstance, seat: string | null, where: string) => void): void {
  const go = (b: BrickInstance, seat: string | null, where: string) => {
    if (b.brick === 'repeat') {
      if (b.select) visit(b, null, `${where}.repeat ${b.select}`);
      const list = b.over === 'opponents' ? seats.filter(x => x !== self) : seats;
      for (const s2 of list) if (b.child) go(b.child, s2, `${where}.child`);
      return;
    }
    const own = b.seat ? (b.seat === '{self}' ? self : b.seat === '{seat}' ? seat : b.seat) : seat;
    visit(b, own, `${where} ${b.select ?? b.brick}`);
    const slots = b.props?.slots;
    if (Array.isArray(slots)) (slots as BrickInstance[]).forEach((child, i) => go(child, own, `${where}.slots[${i}]`));
  };
  for (const [area, list] of Object.entries(s.place)) list.forEach((b, i) => go(b, null, `${area}[${i}]`));
  for (const tab of s.views?.tabs ?? []) {
    const owners = tab.perSeat === 'opponents' ? seats.filter(x => x !== self) : [null];
    for (const owner of owners) tab.bricks.forEach((b, i) => go(b, owner, `views.${tab.id}[${i}]`));
  }
}

/** Checks only declarative public image IDs, never an object's hidden identity. */
export function validateSceneArtwork(file: SceneFile, assets?: Set<string>): Issue[] {
  const c = new Checker('scene.json');
  const check = (source: unknown, path: string) => {
    if (c.string(source, path, 134, /^asset:[A-Za-z0-9][A-Za-z0-9:_./-]{0,127}$/) && assets && !assets.has((source as string).slice(6)))
      c.error(path, 'Artwork asset is not declared.');
  };
  const visit = (b: BrickInstance, path: string) => {
    if (b.brick === 'repeat') { if (b.child) visit(b.child, path + '.child'); return; }
    if (b.brick === 'action-tiles' && Array.isArray(b.props?.images)) {
      const tiles = new Set(((b.props?.tiles as string[] | undefined) ?? []).map(s => s.split('|')[0]));
      const seen = new Set<string>();
      (b.props.images as string[]).forEach((entry, i) => {
        const [id, source, extra] = entry.split('|');
        if (!tiles.has(id) || seen.has(id!) || extra !== undefined) c.error(path + '.images[' + i + ']', 'Use one declared tile ID and asset:ID per image.');
        seen.add(id!); check(source, path + '.images[' + i + ']');
      });
    }
    if (b.brick === 'result-podium') for (const key of ['victoryArt', 'defeatArt', 'neutralArt'])
      if (b.props?.[key] !== undefined) check(b.props[key], path + '.' + key);
    if (b.brick === 'panel' && b.props?.background !== undefined) check(b.props.background, path + '.background');
    if (b.brick === 'opponent-strip' && Array.isArray(b.props?.portraits)) for (const [i, entry] of (b.props.portraits as string[]).entries()) check(entry.split('|')[1], `${path}.portraits[${i}]`);
    if(b.brick==='pile'&&b.props?.backArt!==undefined)check(b.props.backArt,path+'.backArt');
    if(b.brick==='rules-guide')for(const [i,entry] of ((b.props?.legend as string[]|undefined)??[]).entries()){const source=entry.split('|')[1];if(source)check(source,`${path}.legend[${i}]`);}
    if(b.brick==='reference-panel'){
      const keys=new Set<string>();
      for(const [i,entry] of ((b.props?.entries as string[]|undefined)??[]).entries()){
        const parts=entry.split('|');
        if(parts.length!==3||!parts.every(p=>p.trim())||parts[0]!.length>128||parts[1]!.length>64||parts[2]!.length>800||keys.has(parts[0]!))c.error(`${path}.entries[${i}]`,'Use unique value|title|help text entries (128/64/800 characters).');
        keys.add(parts[0]!);
      }
      const images=new Set<string>();
      for(const [i,entry] of ((b.props?.images as string[]|undefined)??[]).entries()){
        const [key,source,extra]=entry.split('|');
        if(!keys.has(key!)||images.has(key!)||extra!==undefined)c.error(`${path}.images[${i}]`,'Use one declared help value and asset:ID per image.');
        images.add(key!);check(source,`${path}.images[${i}]`);
      }
    }
    if (Array.isArray(b.props?.slots)) (b.props.slots as BrickInstance[]).forEach((child, i) => visit(child, path + '.slots[' + i + ']'));
  };
  for (const [name, scene] of [['scene', file.scene], ['narrow', file.narrow], ['solo', file.solo]] as const) {
    if (!scene) continue;
    for (const [area, source] of Object.entries(scene.backgrounds ?? {})) check(source, name + '.backgrounds.' + area);
    for (const [area, list] of Object.entries(scene.place ?? {})) list.forEach((b, i) => visit(b, name + '.place.' + area + '[' + i + ']'));
    scene.views?.tabs.forEach((tab, i) => {
      if (tab.background !== undefined) check(tab.background, name + '.tabs[' + i + '].background');
      tab.bricks.forEach((b, j) => visit(b, name + '.tabs[' + i + '].bricks[' + j + ']'));
    });
  }
  return c.issues;
}

/** Bounds for `rules-guide` (the generic prop checks already reject unknown props and wrong types). */
function rulesGuide(c: Checker, b: BrickInstance, path: string): void {
  const p = b.props ?? {};
  const sections = Array.isArray(p.sections) ? p.sections as unknown[] : [];
  const legend = Array.isArray(p.legend) ? p.legend as unknown[] : [];
  if (!sections.length && !legend.length && p.glossary !== true) c.error(`${path}.props`, 'rules-guide needs sections, legend or glossary.');
  const titles = new Set<string>();
  sections.forEach((entry, i) => {
    const at = typeof entry === 'string' ? entry.indexOf('|') : -1;
    const title = at > 0 ? (entry as string).slice(0, at) : '';
    const text = at > 0 ? (entry as string).slice(at + 1) : '';
    if (!title.trim() || title.length > 64 || !text.trim() || text.length > 1200 || titles.has(title)) c.error(`${path}.props.sections[${i}]`, 'Use unique Title|text sections (64/1200 characters).');
    titles.add(title);
  });
  const terms = new Set<string>();
  legend.forEach((entry, i) => {
    const parts = typeof entry === 'string' ? entry.split('|') : [];
    const [group = '', asset = '', term = ''] = parts;
    const meaning = parts.slice(3).join('|');
    const key = `${group}\u0000${term}`;
    if (parts.length < 4 || !group.trim() || group.length > 32 || !term.trim() || term.length > 48 || !meaning.trim() || meaning.length > 240 || (asset !== '' && !/^asset:[A-Za-z0-9][A-Za-z0-9:_./-]{0,127}$/.test(asset)) || terms.has(key))
      c.error(`${path}.props.legend[${i}]`, 'Use unique group|asset:ID|Term|Meaning rows (32/48/240 characters; the asset may be empty).');
    terms.add(key);
  });
}
